import { ref } from 'vue'
import type { Ref } from 'vue'
import { createTokenStorage } from '@space/shared-api-client'
import type { TmaState } from './index'
import { TelegramAuthService } from './TelegramAuthService'
import type { TelegramAuthResult, TelegramEmailAction } from './TelegramAuthService'
import { clearTelegramInitData, isTelegramMiniAppContext } from './telegramLaunchParams'

export const emailErrorKeys = {
  invalid_email: 'miniApp.email.errors.invalid_email',
  wrong_code: 'miniApp.email.errors.wrong_code',
  expired_code: 'miniApp.email.errors.expired_code',
  cooldown: 'miniApp.email.errors.cooldown',
  stale_revision: 'miniApp.email.errors.stale_revision',
  attempt_limit: 'miniApp.email.errors.attempt_limit',
  send_limit: 'miniApp.email.errors.send_limit',
  delivery_failed: 'miniApp.email.errors.delivery_failed',
  binding_conflict: 'miniApp.email.errors.binding_conflict',
  proof_unavailable: 'miniApp.email.errors.proof_unavailable',
  privacy_unavailable: 'miniApp.email.errors.privacy_unavailable',
  outdated_code: 'miniApp.email.errors.outdated_code',
} as const

export function emailErrorKey(code?: string): string {
  return code && Object.prototype.hasOwnProperty.call(emailErrorKeys, code)
    ? emailErrorKeys[code as keyof typeof emailErrorKeys]
    : 'miniApp.email.errors.generic'
}

/** Server owns proof and the current step; this controller only accepts fresh responses. */
export function createTmaAuthFlow(state: Ref<TmaState>, t: (key: string) => string, onRedirect?: (path: string) => void) {
  const busy = ref(false)
  const error = ref('')
  const modalOpen = ref(true)
  const skippedEmail = ref(false)
  const tokenStorage = createTokenStorage({ storageKey: import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken' })
  let generation = 0

  function handleInvalidInitData(isCurrent: () => boolean): boolean {
    if (!isCurrent()) return true
    const inTelegram = isTelegramMiniAppContext()
    const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'
    tokenStorage.clearToken()
    localStorage.removeItem(storageKey)
    for (const key of ['user_avatar', 'partner_bonus_split', 'is_ambassador']) localStorage.removeItem(key)
    Object.keys(localStorage).filter(key => key.startsWith('/api/matrix/')).forEach(key => localStorage.removeItem(key))
    clearTelegramInitData()
    const cookieNames = ['dasdasdaddjdj', '_dasdasdaddjdj', 'PHPSESSID', 'tma_user']
    const hostname = window.location.hostname
    const domains = hostname ? [undefined, hostname, `.${hostname.split('.').slice(-2).join('.')}`] : [undefined]
    for (const name of cookieNames) {
      for (const domain of domains) {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain ? `; domain=${domain}` : ''}`
      }
    }
    state.value.authResult = null
    state.value.initData = ''
    state.value.status = 'error'
    state.value.errorMessage = t('tma.authError')
    state.value.abortedToLanding = !inTelegram
    onRedirect?.(inTelegram ? '/tma-init' : '/')
    return true
  }

  async function apply(result: TelegramAuthResult, isCurrent: () => boolean) {
    if (!isCurrent()) return
    if (result.result === 'error') {
      if (result.message === 'invalid_init_data') { handleInvalidInitData(isCurrent); return }
      if (state.value.status === 'email_required') error.value = t(emailErrorKey(result.message))
      else {
        state.value.status = 'error'
        state.value.errorMessage = t('tma.authError')
      }
      return
    }
    if (result.result === 'success') {
      if (!result.spa_auth_token) throw new Error('Missing auth token')
      // The guarded transport also prevents a stale exchange's auth headers from being stored.
      const token = await TelegramAuthService.exchangeToken(result.spa_auth_token, isCurrent)
      if (!isCurrent()) return
      tokenStorage.setToken(token)
      state.value.authResult = result
      state.value.status = 'done'
      return
    }
    if (result.result === 'email_required') {
      if (!result.email_flow || !['confirm_email', 'email', 'code', 'error', 'protected_account'].includes(result.email_flow.screen)) {
        throw new Error('Invalid email flow')
      }
    }
    if (!['email_required', 'sponsor_required', 'totp_required'].includes(result.result)) {
      throw new Error('Invalid auth result')
    }
    state.value.authResult = result
    state.value.status = result.result
    modalOpen.value = true
  }

  async function run(request: () => Promise<TelegramAuthResult>) {
    const current = ++generation
    const isCurrent = () => current === generation
    busy.value = true
    error.value = ''
    try {
      await apply(await request(), isCurrent)
    } catch (failure: unknown) {
      if (!isCurrent()) return
      const data = (failure as { response?: { data?: TelegramAuthResult } })?.response?.data
      if (data?.result === 'error' && data.message === 'invalid_init_data') { handleInvalidInitData(isCurrent); return }
      // HTTP failures may carry a fresh recovery revision, NEVER a session or
      // a first-factor success. Only allow known continuation/error envelopes.
      if (data?.result === 'email_required' && data.email_flow
        && ['confirm_email', 'email', 'code', 'error', 'protected_account'].includes(data.email_flow.screen)) {
        state.value.authResult = { result: 'email_required', email_flow: data.email_flow }
        state.value.status = 'email_required'
        error.value = data.email_flow.error ? t(emailErrorKey(data.email_flow.error)) : t('miniApp.email.errors.generic')
      } else if (state.value.status === 'email_required') error.value = t(emailErrorKey(data?.result === 'error' ? data.message : undefined))
      else {
        state.value.status = 'error'
        state.value.errorMessage = t('tma.authError')
      }
    } finally {
      if (isCurrent()) busy.value = false
    }
  }

  async function authenticate(initData: string) {
    // A browser session must never choose the account for signed Telegram auth.
    // Pending TOTP proof stays only in authResult, never in session storage.
    tokenStorage.clearToken()
    skippedEmail.value = false
    modalOpen.value = true
    state.value.authResult = null
    state.value.initData = initData
    state.value.status = 'authenticating'
    state.value.errorMessage = ''
    state.value.abortedToLanding = false
    await run(() => TelegramAuthService.authenticate(initData))
  }

  async function accept(result: TelegramAuthResult) {
    if (busy.value || state.value.status === 'done') return
    await run(async () => result)
  }

  async function input(text: string) {
    const screen = state.value.authResult?.email_flow?.screen
    if (state.value.status !== 'email_required' || !['email', 'code'].includes(screen ?? '') || busy.value || !text.trim()) return
    await run(() => TelegramAuthService.emailInput(state.value.initData, text.trim()))
  }

  async function action(action: Exclude<TelegramEmailAction, 'cancel'>) {
    const revision = state.value.authResult?.email_flow?.revision
    if (state.value.status !== 'email_required' || !revision || (busy.value && action !== 'change_email' && action !== 'cancel_change')) return
    if (action === 'skip' && state.value.authResult?.email_flow?.can_skip !== true) return
    const current = generation + 1
    await run(async () => {
      const result = await TelegramAuthService.emailAction(state.value.initData, `${action}:${revision}`)
      if (current === generation && action === 'skip' && (result.result === 'success' || result.result === 'totp_required')) skippedEmail.value = true
      return result
    })
  }

  async function close() {
    const revision = state.value.authResult?.email_flow?.revision
    const current = ++generation // Invalidate input, action, or exchange before cancel.
    // The continuation must wait for the server's replacement revision before
    // skip/resend can run; the old revision is being cancelled right now.
    busy.value = !!revision
    error.value = ''
    modalOpen.value = false
    if (revision) {
      try {
        const result = await TelegramAuthService.emailAction(state.value.initData, `cancel:${revision}`)
        if (current === generation && result.result === 'email_required' && result.email_flow) {
          state.value.authResult = result
          state.value.status = 'email_required'
        }
      } catch (failure: unknown) {
        const data = (failure as { response?: { data?: TelegramAuthResult } })?.response?.data
        if (current === generation && data?.result === 'email_required' && data.email_flow
          && ['confirm_email', 'email', 'code', 'error', 'protected_account'].includes(data.email_flow.screen)) {
          state.value.authResult = { result: 'email_required', email_flow: data.email_flow }
          state.value.status = 'email_required'
        }
      } finally { if (current === generation) busy.value = false }
    }
  }

  async function resume() {
    if (busy.value) return
    if (!state.value.authResult?.email_flow?.revision) {
      await authenticate(state.value.initData)
      return
    }
    modalOpen.value = true
  }

  return { busy, error, modalOpen, skippedEmail, authenticate, accept, input, action, close, resume }
}

export type TmaAuthFlow = ReturnType<typeof createTmaAuthFlow>
